import { useState } from 'react';

import { Alert } from '@/components/ui/alert';
import { Button } from '@/components/ui/button';
import { Dialog } from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { formatDateValue, toNumber } from '@/lib/utils';

import { useCrearCodigoCalibracion } from '../hooks/use-calibracion';
import { vigenciaCodigo } from '../lib/calibracion-format';
import type { CodigoCalibracion } from '../calibracion.types';

interface GenerarCodigoDialogProps {
  open: boolean;
  onClose: () => void;
}

/** Valores por defecto del formulario (los mismos del panel anterior). */
const DURACION_DEFECTO = '30';
const USOS_DEFECTO = '1';
const MAX_DURACION_DIAS = 3650;

/**
 * Genera un código de calibración.
 *
 * Tras crearlo se muestra el código en grande (es el dato que el operador
 * necesita anotar) con un botón para copiarlo y otro para generar el siguiente.
 */
export function GenerarCodigoDialog({ open, onClose }: GenerarCodigoDialogProps) {
  const crear = useCrearCodigoCalibracion();

  const [duracion, setDuracion] = useState(DURACION_DEFECTO);
  const [usos, setUsos] = useState(USOS_DEFECTO);
  const [descripcion, setDescripcion] = useState('');
  const [errores, setErrores] = useState<Record<string, string>>({});
  const [aviso, setAviso] = useState<string | null>(null);
  const [creado, setCreado] = useState<CodigoCalibracion | null>(null);
  const [copiado, setCopiado] = useState(false);

  /** Reinicia el formulario para generar otro código sin cerrar el diálogo. */
  const reiniciar = () => {
    setDuracion(DURACION_DEFECTO);
    setUsos(USOS_DEFECTO);
    setDescripcion('');
    setErrores({});
    setAviso(null);
    setCreado(null);
    setCopiado(false);
  };

  const cerrar = () => {
    reiniciar();
    onClose();
  };

  const enviar = () => {
    const dias = toNumber(duracion);
    const cantidad = toNumber(usos);
    const encontrados: Record<string, string> = {};

    if (!(dias >= 1 && dias <= MAX_DURACION_DIAS)) {
      encontrados.duracion = `Indica entre 1 y ${MAX_DURACION_DIAS} días.`;
    }

    if (!(cantidad >= 1 && cantidad <= 1000)) {
      encontrados.usos = 'Indica entre 1 y 1000 usos.';
    }

    setErrores(encontrados);
    setAviso(null);

    if (Object.keys(encontrados).length > 0) {
      setAviso('Revisa los campos marcados.');
      return;
    }

    crear.mutate(
      {
        duracion_dias: dias,
        cantidad_usos: cantidad,
        descripcion: descripcion.trim() || null,
      },
      {
        onSuccess: (respuesta) => setCreado(respuesta.codigo),
        onError: (error) => setAviso(error.message || 'No se pudo generar el código.'),
      },
    );
  };

  const copiar = async () => {
    if (!creado) return;

    try {
      await navigator.clipboard.writeText(creado.codigo);
      setCopiado(true);
    } catch {
      setAviso('El navegador no permitió copiar. Anota el código manualmente.');
    }
  };

  return (
    <Dialog
      open={open}
      onClose={cerrar}
      title={creado ? 'Código generado' : 'Generar código de calibración'}
      description={
        creado
          ? 'Anota el código: autoriza la calibración durante la vigencia indicada.'
          : 'El código de 6 dígitos queda activo hasta que venza o se agoten sus usos.'
      }
      size="sm"
      footer={
        creado ? (
          <>
            <Button variant="outline" size="sm" onClick={reiniciar}>
              Generar otro
            </Button>
            <Button size="sm" onClick={cerrar}>
              Listo
            </Button>
          </>
        ) : (
          <>
            <Button variant="outline" size="sm" onClick={cerrar} disabled={crear.isPending}>
              Cancelar
            </Button>
            <Button size="sm" loading={crear.isPending} onClick={enviar}>
              Generar código
            </Button>
          </>
        )
      }
    >
      {aviso ? (
        <Alert variant="error" title="No se pudo generar">
          {aviso}
        </Alert>
      ) : null}

      {creado ? (
        <div className="space-y-4">
          <div className="rounded-card border border-brand-200 bg-brand-50/70 px-4 py-5 text-center">
            <p className="text-[11px] font-semibold uppercase tracking-wider text-brand-700">
              Código
            </p>
            <p className="mt-1 font-mono text-4xl font-bold tracking-[0.3em] text-brand-800 tabular-nums">
              {creado.codigo}
            </p>
          </div>

          <dl className="space-y-2 text-sm">
            <div className="flex items-center justify-between gap-3">
              <dt className="text-ink-500">Usos</dt>
              <dd className="font-medium text-ink-800">
                {creado.cantidad_usos} {creado.cantidad_usos === 1 ? 'uso' : 'usos'}
              </dd>
            </div>
            <div className="flex items-center justify-between gap-3">
              <dt className="text-ink-500">Expira</dt>
              <dd className="font-medium text-ink-800">
                {formatDateValue(creado.fecha_expiracion)}
              </dd>
            </div>
            <div className="flex items-center justify-between gap-3">
              <dt className="text-ink-500">Vigencia</dt>
              <dd className="font-medium text-ink-800">{vigenciaCodigo(creado)}</dd>
            </div>
          </dl>

          <Button
            variant="outline"
            size="sm"
            fullWidth
            onClick={() => void copiar()}
            disabled={copiado}
          >
            {copiado ? 'Copiado al portapapeles' : 'Copiar código'}
          </Button>
        </div>
      ) : (
        <div className="space-y-4">
          <div className="grid gap-4 sm:grid-cols-2">
            <Input
              label="Duración (días)"
              type="number"
              min={1}
              max={MAX_DURACION_DIAS}
              inputMode="numeric"
              value={duracion}
              error={errores.duracion}
              onChange={(event) => setDuracion(event.target.value)}
            />

            <Input
              label="Cantidad de usos"
              type="number"
              min={1}
              max={1000}
              inputMode="numeric"
              value={usos}
              error={errores.usos}
              hint="Cuántas calibraciones autoriza."
              onChange={(event) => setUsos(event.target.value)}
            />
          </div>

          <Input
            label="Descripción (opcional)"
            value={descripcion}
            maxLength={120}
            placeholder="Ej. Calibración de lados 3 y 4"
            onChange={(event) => setDescripcion(event.target.value)}
          />
        </div>
      )}
    </Dialog>
  );
}
